<script lang="ts">
export type VPTextInputDialogOutcome = { action: 'confirm'; value: string } | { action: 'cancel' };
</script>

<script setup lang="ts">
import { type Ref, computed, inject, nextTick, onMounted, useId, useTemplateRef, watch } from 'vue';
import { useForm } from 'vee-validate';
import { useGettext } from 'vue3-gettext';
import VPButton from './VPButton.vue';
import VPModal from './VPModal.vue';
import VPInput from './form/VPInput.vue';

type Props = {
  show: boolean;
  title: string;
  message?: string;
  label: string;
  initialValue?: string;
  placeholder?: string;
  confirmLabel: string;
  cancelLabel?: string;
  required?: boolean;
  requiredMessage?: string;
  zIndexLevel?: number;
};

const props = withDefaults(defineProps<Props>(), {
  message: undefined,
  initialValue: '',
  placeholder: undefined,
  cancelLabel: undefined,
  required: false,
  requiredMessage: undefined,
  zIndexLevel: 1,
});

const emit = defineEmits<{
  (e: 'update:show', show: boolean): void;
  (e: 'close', outcome: VPTextInputDialogOutcome): void;
}>();

const { $gettext } = useGettext();

const teleportTarget = inject<Readonly<Ref<HTMLElement | null>> | null>('vpModalTeleportTarget', null);
const titleId = useId();
const fieldId = useId();
const input = useTemplateRef<InstanceType<typeof VPInput>>('input');

const requiredText = computed(() => props.requiredMessage ?? $gettext('This field is required'));

const { handleSubmit, resetForm } = useForm<{ value: string }>({
  initialValues: { value: props.initialValue },
  validationSchema: {
    value: (value: unknown) => !props.required || !!String(value ?? '').trim() || requiredText.value,
  },
});

let closing = false;

// Selected like `prompt()`'s default, so typing replaces the initial value instead of appending to it.
const focusField = () => {
  input.value?.focus();
  (input.value?.$el as HTMLElement | undefined)?.querySelector('input')?.select();
};

onMounted(focusField);

watch(() => props.show, async open => {
  if (open) {
    closing = false;
    resetForm({ values: { value: props.initialValue } });
    await nextTick();
    focusField();
  }
});

const emitClose = (outcome: VPTextInputDialogOutcome) => {
  if (closing) {
    return;
  }

  closing = true;
  emit('update:show', false);
  emit('close', outcome);
};

const cancel = () => emitClose({ action: 'cancel' });

const confirm = handleSubmit(values => emitClose({ action: 'confirm', value: values.value ?? '' }));
</script>

<template>
  <VPModal
    :show="show"
    :teleport-target="teleportTarget ?? undefined"
    :z-index-level="zIndexLevel"
    hide-actions
    role="dialog"
    aria-modal="true"
    :aria-labelledby="titleId"
    class="w-104 max-w-full min-w-0 rounded-md border border-neutral-300 bg-neutral-0 p-0 gap-0 shadow-vp-large"
    @close="cancel"
    @backdrop-click="cancel"
  >
    <form
      class="flex flex-col gap-6 px-6 pt-4 pb-8"
      novalidate
      @submit.prevent="confirm"
    >
      <div class="flex flex-col gap-4">
        <div class="flex items-center justify-between gap-4">
          <h2
            :id="titleId"
            class="text-2xl font-bold text-neutral-900"
          >
            {{ title }}
          </h2>
          <VPButton
            variant="ghost-neutral"
            size="sm"
            icon-prefix="mdi:close"
            class="-m-2 text-2xl!"
            :aria-label="$gettext('Close')"
            @click="cancel"
          />
        </div>
        <div
          v-if="message || $slots.default"
          class="text-base text-neutral-700"
        >
          <slot>{{ message }}</slot>
        </div>
      </div>

      <VPInput
        :id="fieldId"
        ref="input"
        name="value"
        :label="label"
        :input-attrs="{ placeholder, autocomplete: 'off' }"
      />

      <div class="flex gap-4">
        <VPButton
          v-if="cancelLabel"
          variant="outline"
          size="lg"
          class="flex-1"
          type="button"
          :label="cancelLabel"
          @click="cancel"
        />
        <VPButton
          variant="default"
          size="lg"
          class="flex-1"
          type="submit"
          :label="confirmLabel"
        />
      </div>
    </form>
  </VPModal>
</template>
